import Phaser from 'phaser';
import { GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { isSupabaseConfigured, onAuthChange } from '@/services/supabaseClient';
import { onDiscordAuthChange } from '@/discord';
import { fetchAccountStatus } from '@/services/accountStatus';
import { isDiscordActivity } from '@/utils/discordActivity';
import { captureError } from '@/sentry';

export function mountAccountBadge(
  scene: Phaser.Scene,
  options?: { x?: number; y?: number }
): Phaser.GameObjects.Text {
  const x = options?.x ?? 40;
  const y = options?.y ?? GAME_HEIGHT - 26;

  const label = scene.add.text(x, y, 'Account: checking…', {
    fontFamily: THEME.fonts.pixel,
    fontSize: THEME.sizes.small,
    color: THEME.colors.textMuted,
  }).setOrigin(0, 0.5).setDepth(1000);

  const unsubs: Array<() => void> = [];

  const refresh = () => {
    fetchAccountStatus()
      .then((status) => {
        let text: string;
        if (status.state === 'signed_in') {
          if (status.isDiscord && status.discordUsername) {
            text = status.discordUsername;
          } else if (status.isDiscord) {
            text = 'Discord';
          } else {
            text = status.email;
          }
        } else if (status.state === 'offline') {
          text = 'Offline';
        } else {
          text = 'No account';
        }
        label.setText(text);
      })
      .catch((error) => {
        captureError(error, { scope: 'accountBadge', action: 'refresh' });
        label.setText('Error');
      });
  };

  refresh();

  // Subscribe to auth changes - Discord in Activity mode, Supabase otherwise
  if (isDiscordActivity()) {
    unsubs.push(onDiscordAuthChange(() => refresh()));
  }
  if (isSupabaseConfigured()) {
    unsubs.push(onAuthChange(() => refresh()));
  }

  scene.events.once('shutdown', () => {
    for (const unsub of unsubs) {
      unsub();
    }
    unsubs.length = 0;
    label.destroy();
  });

  return label;
}
